import React, { useState, useEffect } from 'react';
import { CheckCircle, FlaskConical, XCircle, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../api';
import { ConfirmDeleteModal } from '../components/modals/ConfirmDeleteModal';

const LabRequestsPage = ({ user }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReq, setSelectedReq] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const isAdmin = user.role === 'admin';

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const data = await api.request('/lab-requests');
      setRequests(Array.isArray(data) ? data : []);
    } catch { setRequests([]); }
    setLoading(false);
  };

  useEffect(() => { fetchRequests(); }, []);

  const handleReview = async (reqId, status) => {
    setSubmitting(true); setError('');
    try {
      await api.request(`/lab-requests/${reqId}/review`, {
        method: 'PUT',
        body: JSON.stringify({ status, admin_notes: reviewNotes })
      });
      setSuccess(`Request ${status} successfully. Email sent to requester.`);
      setSelectedReq(null); setReviewNotes('');
      fetchRequests();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) { setError(err.message); }
    setSubmitting(false);
  };

  const statusBadge = (s) => {
    const map = {
      pending:  'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
    };
    return <span className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${map[s] || 'bg-gray-100 text-gray-700'}`}>{s}</span>;
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === requests.length) setSelectedIds([]);
    else setSelectedIds(requests.map(r => r.id));
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      if (deleteTarget.ids.length === 1) {
        await api.request(`/lab-requests/${deleteTarget.ids[0]}`, { method: 'DELETE' });
      } else {
        await api.request('/lab-requests/bulk-delete', {
          method: 'POST',
          body: JSON.stringify({ ids: deleteTarget.ids }),
        });
      }
      toast.success(`${deleteTarget.ids.length} lab request(s) moved to Deleted Items.`);
      setSelectedIds(prev => prev.filter(id => !deleteTarget.ids.includes(id)));
      setDeleteTarget(null);
      fetchRequests();
    } catch (err) {
      toast.error(err.message || 'Failed to delete lab request(s).');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-4" data-testid="lab-requests-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-levelup-dark flex items-center gap-3">
            <FlaskConical size={26} className="text-blue-600" />
            Lab Requests
          </h1>
          <p className="text-gray-500 text-sm mt-1">Review and action interactive simulated lab provisioning requests</p>
        </div>
        <button onClick={fetchRequests} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors">
          Refresh
        </button>
      </div>

      {success && <div className="bg-green-50 border border-green-200 text-green-700 px-3 py-2 rounded-lg text-sm">{success}</div>}
      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">{error}</div>}

      {isAdmin && selectedIds.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center justify-between" data-testid="lab-requests-bulk-action-bar">
          <span className="text-sm text-red-700 font-medium">{selectedIds.length} request(s) selected</span>
          <div className="flex items-center gap-2">
            <button onClick={() => setSelectedIds([])} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800">
              Clear selection
            </button>
            <button
              onClick={() => setDeleteTarget({ ids: selectedIds })}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700"
              data-testid="bulk-delete-lab-requests-btn"
            >
              <Trash2 size={14} /> Delete Selected
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-20 text-gray-400">Loading requests...</div>
      ) : requests.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-16 text-center">
          <FlaskConical size={40} className="text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 font-medium">No lab requests yet</p>
          <p className="text-gray-400 text-sm mt-1">Requests will appear here when partners schedule workshops with lab activities</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {isAdmin && (
                  <th className="px-3 py-2 w-8">
                    <input
                      type="checkbox"
                      checked={requests.length > 0 && selectedIds.length === requests.length}
                      onChange={toggleSelectAll}
                      data-testid="select-all-lab-requests-checkbox"
                    />
                  </th>
                )}
                {['Course', 'Organization', 'Requester', 'Workshop Date', 'Lab Attendees', 'Status', 'Submitted', 'Action'].map(h => (
                  <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                ))}
                {isAdmin && <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Delete</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {requests.map(req => (
                <tr key={req.id} className="hover:bg-gray-50 transition-colors">
                  {isAdmin && (
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(req.id)}
                        onChange={() => toggleSelect(req.id)}
                        data-testid={`select-lab-request-${req.id}`}
                      />
                    </td>
                  )}
                  <td className="px-3 py-2 font-medium text-gray-900 max-w-[180px] truncate" title={req.course_title}>{req.course_title}</td>
                  <td className="px-3 py-2 text-gray-600">{req.organization || req.partner_domain}</td>
                  <td className="px-3 py-2 text-gray-600">
                    <div>{req.requester_name}</div>
                    <div className="text-xs text-gray-400">{req.requester_email}</div>
                  </td>
                  <td className="px-3 py-2 text-gray-600">{req.workshop_date ? req.workshop_date.split('T')[0] : '—'}</td>
                  <td className="px-3 py-2 text-center font-semibold text-gray-800">{req.expected_lab_attendees}</td>
                  <td className="px-3 py-2">{statusBadge(req.status)}</td>
                  <td className="px-3 py-2 text-gray-400 text-xs">{req.created_at ? new Date(req.created_at).toLocaleDateString() : '—'}</td>
                  <td className="px-3 py-2">
                    {req.status === 'pending' && ['admin', 'ms_stakeholder'].includes(user.role) ? (
                      <button
                        onClick={() => { setSelectedReq(req); setReviewNotes(''); setError(''); }}
                        className="px-3 py-1.5 bg-levelup-dark text-white rounded-lg text-xs font-semibold hover:bg-levelup-teal transition-colors"
                        data-testid={`review-btn-${req.id}`}
                      >
                        Review
                      </button>
                    ) : (
                      <span className="text-gray-400 text-xs">{req.reviewed_by ? `by ${req.reviewed_by}` : '—'}</span>
                    )}
                  </td>
                  {isAdmin && (
                    <td className="px-3 py-2">
                      <button
                        onClick={() => setDeleteTarget({ ids: [req.id] })}
                        className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete lab request"
                        data-testid={`delete-lab-request-${req.id}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Review Modal */}
      {selectedReq && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl" data-testid="review-modal">
            <div className="p-6 border-b">
              <h3 className="text-lg font-bold text-levelup-dark flex items-center gap-2">
                <FlaskConical size={20} className="text-blue-600" />
                Review Lab Request
              </h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-gray-500">Course</span><span className="font-medium text-gray-800">{selectedReq.course_title}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Organization</span><span className="font-medium">{selectedReq.organization || selectedReq.partner_domain}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Requester</span><span className="font-medium">{selectedReq.requester_name} ({selectedReq.requester_email})</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Workshop Date</span><span className="font-medium">{selectedReq.workshop_date ? selectedReq.workshop_date.split('T')[0] : '—'}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Location</span><span className="font-medium">{selectedReq.location}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Workshop Attendees</span><span className="font-medium">{selectedReq.expected_attendees}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Lab Attendees</span><span className="font-bold text-blue-700">{selectedReq.expected_lab_attendees}</span></div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional — sent to requester on rejection)</label>
                <textarea
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-levelup-accent"
                  placeholder="Add notes or reason for rejection..."
                  value={reviewNotes}
                  onChange={e => setReviewNotes(e.target.value)}
                  data-testid="review-notes-input"
                />
              </div>
              {error && <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">{error}</div>}
            </div>
            <div className="p-6 border-t flex justify-between">
              <button onClick={() => setSelectedReq(null)} className="px-4 py-2 text-gray-500 hover:text-gray-700 transition-colors">Cancel</button>
              <div className="flex gap-3">
                <button
                  onClick={() => handleReview(selectedReq.id, 'rejected')}
                  disabled={submitting}
                  className="flex items-center gap-2 px-5 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors disabled:opacity-50"
                  data-testid="reject-btn"
                >
                  <XCircle size={16} />
                  {submitting ? '...' : 'Reject'}
                </button>
                <button
                  onClick={() => handleReview(selectedReq.id, 'approved')}
                  disabled={submitting}
                  className="flex items-center gap-2 px-5 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors disabled:opacity-50"
                  data-testid="approve-btn"
                >
                  <CheckCircle size={16} />
                  {submitting ? '...' : 'Approve'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmDeleteModal
        open={!!deleteTarget}
        title="Delete lab request(s)?"
        itemCount={deleteTarget?.ids.length || 0}
        deleting={deleting}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
};

export default LabRequestsPage;
